import React, { ReactNode, ComponentType } from 'react';
import { Box, Button, SxProps } from '@mui/material';
import { useResponsive } from '../../contexts/ResponsiveContext';
import SustainabilityContainer from '../sustainabilityPage/SustainabilityContainer';
import PicContent from '../sustainabilityPage/PicContent';

/** The minimal container used for actual ResponsiveSplitSection content on big-screens */
export interface MinimalContentContainerProps {
    children: ReactNode;
    backgroundColor?: string;
}

export interface ResponsiveSplitSectionProps<ContentContainerProps = {}> {
    textContent: ReactNode;
    backgroundColor?: string;
    smallScreenTextFirst?: boolean;
    imageSrc: string;
    buttonText?: string;
    buttonHref?: string;
    buttonHref2?: string;
    buttonText3?: string;
    buttonHref3?: string;
    buttonText4?: string;
    buttonHref4?: string;
    buttonText5?: string;
    buttonHref5?: string;
    buttonSx?: SxProps;
    buttonText2?: string;
    bigScreenAlignItem?: string;
    bigScreenTextLeft?: boolean; // New prop to control content order on big screens
    /** Optional override of the big-screen container (defaults to SustainabilityContainer). */
    ContentContainer?: ComponentType<MinimalContentContainerProps & ContentContainerProps>;
    contentContainerProps?: ContentContainerProps;
}

const ResponsiveSplitSection = <ContentContainerProps = {},>({
    textContent,
    backgroundColor = 'white',
    smallScreenTextFirst = true,
    imageSrc,
    buttonText,
    buttonHref,
    buttonText2,
    buttonHref2,
    buttonText3,
    buttonHref3,
    buttonText4,
    buttonHref4,
    buttonText5,
    buttonHref5,
    buttonSx,
    bigScreenAlignItem = 'flex-start',
    bigScreenTextLeft = true, // Default to true
    ContentContainer: Container, // defaults to SustainabilityContainer, set inside the function
    contentContainerProps: containerProps,
}: ResponsiveSplitSectionProps<ContentContainerProps>) => {
    const ActualContentContainer = (Container ?? SustainabilityContainer) as ComponentType<
        MinimalContentContainerProps & ContentContainerProps
    >;
    const { isScreenMd, isScreenSm, isScreenXs, responsiveWidth } = useResponsive();
    const isSmallScreen = isScreenMd || isScreenSm || isScreenXs;

    if (isSmallScreen) {
        const picContent = (
            <>
                <img
                    src={imageSrc}
                    width="650px"
                    height="298px"
                    alt="image"
                    style={{ borderRadius: '8px' }}
                />
                {(buttonText || buttonText2 || buttonText3 || buttonText4 || buttonText5) && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            justifyContent: 'center',
                            gap: '8px',
                            marginTop: '2rem',
                            marginBottom: '2rem',
                        }}
                    >
                        {buttonText && (
                            <Button
                                variant="contained"
                                sx={buttonSx}
                                href={buttonHref ?? ''}
                                target="_blank"
                            >
                                {buttonText}
                            </Button>
                        )}
                        {buttonText2 && (
                            <Button
                                variant="contained"
                                sx={buttonSx}
                                href={buttonHref2 ?? ''}
                                target="_blank"
                            >
                                {buttonText2}
                            </Button>
                        )}
                        {buttonText3 && (
                            <Button
                                variant="contained"
                                sx={buttonSx}
                                href={buttonHref3 ?? ''}
                                target="_blank"
                            >
                                {buttonText3}
                            </Button>
                        )}
                        {buttonText4 && (
                            <Button
                                variant="contained"
                                sx={buttonSx}
                                href={buttonHref4 ?? ''}
                                target="_blank"
                            >
                                {buttonText4}
                            </Button>
                        )}
                        {buttonText5 && (
                            <Button
                                variant="contained"
                                sx={buttonSx}
                                href={buttonHref5 ?? ''}
                                target="_blank"
                            >
                                {buttonText5}
                            </Button>
                        )}
                    </Box>
                )}
            </>
        );

        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    padding: '0',
                    width: '100%',
                    boxSizing: 'border-box',
                    flexDirection: 'column',
                    overflowY: 'hidden',
                    backgroundColor: backgroundColor,
                }}
            >
                <Box
                    sx={{
                        width: responsiveWidth,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        paddingTop: '2rem',
                        paddingBottom: '4rem',
                        overflowY: 'hidden',
                        margin: 'auto',
                        backgroundColor,
                    }}
                >
                    {smallScreenTextFirst ? (
                        <>
                            {textContent}
                            {picContent}
                        </>
                    ) : (
                        <>
                            {picContent}
                            {textContent}
                        </>
                    )}
                </Box>
            </Box>
        );
    } else {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    padding: '0',
                    width: '100%',
                    boxSizing: 'border-box',
                    flexDirection: 'column',
                    overflowY: 'hidden',
                    backgroundColor: backgroundColor,
                }}
            >
                <ActualContentContainer
                    backgroundColor={backgroundColor}
                    {...(containerProps as ContentContainerProps)}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: bigScreenAlignItem,
                            backgroundColor,
                            width: '100%',
                            boxSizing: 'border-box',
                            paddingTop: '1rem',
                            paddingBottom: '1rem',
                            margin: 'auto',
                        }}
                    >
                        {bigScreenTextLeft && (
                            <Box sx={{ flex: 1, marginRight: '0.5rem' }}>{textContent}</Box>
                        )}

                        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                            <PicContent
                                name="overview"
                                src={imageSrc}
                                buttonSx={buttonSx}
                                gap="1.5rem"
                                buttonText={buttonText}
                                buttonHref={buttonHref}
                                buttonText2={buttonText2}
                                buttonHref2={buttonHref2}
                                buttonText3={buttonText3}
                                buttonHref3={buttonHref3}
                                buttonText4={buttonText4}
                                buttonHref4={buttonHref4}
                                buttonText5={buttonText5}
                                buttonHref5={buttonHref5}
                            />
                        </Box>
                        {!bigScreenTextLeft && (
                            <Box sx={{ flex: 1, marginLeft: '0.5rem' }}>{textContent}</Box>
                        )}
                    </Box>
                </ActualContentContainer>
            </Box>
        );
    }
};

export default ResponsiveSplitSection;
